import React, { useState } from "react";
import { Button, notification } from "antd";
import {
   NO_SUBSCRIPTION_LEFT_MESSAGE,
   isBilledUntilScheduledEnd,
   isCancellationScheduled,
   recoverFromNoSubscription,
   scheduledEndOf
} from "./billingActions";
import { billingApi } from "./billingApi";
import { SubscriptionStatus, describeBillingError } from "./billingCodes";
import { formatBillingDate, formatBillingDateTime, isBillingDateAhead } from "./billingDates";
import { settleWaitingPayment } from "./paymentAction";
import { isPlanDowngradesEnabled } from "./planDowngrades";
import "./index.scss";

// What the subscription page has to say about an organization that is still
// let in, on the new billing flow: a trial running down, a payment that failed
// but is still being forgiven, a payment waiting on the bank, a subscription set
// to end with its period, a plan change waiting for the period to end. Each
// says what to do about it, and offers it.
//
// An organization held at billing_only never gets here — the routes send it to
// the billing page instead.

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

function TrialNotice({ status, onAddCard }) {
   const trialEnd = formatBillingDate(status.trialEnd);
   const daysLeft =
      typeof status.trialDaysLeft === "number"
         ? `${plural(status.trialDaysLeft, "day")} left`
         : null;

   return (
      <div className="billing-notice">
         <div className="billing-notice__text">
            <b>Free trial{daysLeft ? ` — ${daysLeft}` : ""}</b>
            <span>
               {trialEnd ? `Your trial ends on ${trialEnd}. ` : ""}
               {status.hasPaymentMethod
                  ? "Your card is charged when it ends."
                  : "Add a card before then so KAPsurvey carries on without a break."}
            </span>
         </div>
         {!status.hasPaymentMethod && (
            <Button className="kapSurveyPrimaryButton" onClick={onAddCard}>
               Add a card
            </Button>
         )}
      </div>
   );
}

function PaymentDueNotice({ organizationId, status, onAddCard, onChanged }) {
   const [isConfirming, setIsConfirming] = useState(false);
   // The backend reports when the grace period ends only while it runs. It says
   // how long access lasts, not whether there is any: accessLevel decides that,
   // and the routes have already acted on it by the time this page shows.
   const accessEnds = isBillingDateAhead(status.graceEndsAt)
      ? formatBillingDateTime(status.graceEndsAt)
      : null;

   const confirmPayment = async () => {
      if (isConfirming) return;
      setIsConfirming(true);

      try {
         const settled = await settleWaitingPayment(status, organizationId);

         if (settled.error) {
            const reason = settled.error.message ?? describeBillingError({});
            notification.error({
               message: "Oops! A problem has occurred!",
               description: settled.isPlanChange ? `${reason} Your plan hasn't changed.` : reason
            });
         } else if (settled.needsHostedPage && settled.status?.hostedInvoiceUrl) {
            window.open(settled.status.hostedInvoiceUrl, "_blank", "noopener,noreferrer");
         }

         onChanged?.();
      } catch (failure) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      } finally {
         setIsConfirming(false);
      }
   };

   return (
      <div className="billing-notice billing-notice--warning">
         <div className="billing-notice__text">
            <b>
               {status.requiresAction
                  ? "Your bank needs you to confirm a payment"
                  : "Your last payment didn't go through"}
            </b>
            <span>
               Settle it to keep your access.
               {accessEnds
                  ? ` Full access until ${accessEnds}, then only billing is available.`
                  : ""}
            </span>
         </div>
         {status.requiresAction ? (
            <Button
               className="kapSurveyPrimaryButton"
               onClick={confirmPayment}
               loading={isConfirming}
            >
               Confirm payment
            </Button>
         ) : (
            <Button className="kapSurveyPrimaryButton" onClick={onAddCard}>
               Update card
            </Button>
         )}
      </div>
   );
}

// A cancellation ends the subscription at `cancelAt`: on an annual plan
// cancelled before its last month, the end of its 12-month commitment, billed
// monthly until then. The period's end stands in only when there is no
// `cancelAt`. Kept again, the status undo-cancel answers has neither, and the
// notice goes.
function EndingNotice({ organizationId, status, onChanged }) {
   const [isUndoing, setIsUndoing] = useState(false);
   const endsAt = formatBillingDate(scheduledEndOf(status));

   const keepSubscription = async () => {
      if (isUndoing) return;
      setIsUndoing(true);

      try {
         const kept = await billingApi.undoCancel({ organizationId });
         notification.success({
            message: "Subscription kept successfully",
            description: kept?.commitmentEnd
               ? "Your annual plan carries on, billed monthly as before."
               : "It renews as usual at the end of the current period."
         });
         onChanged?.();
      } catch (failure) {
         // Stripe no longer has the subscription to keep: the billing page's
         // plans take over (billingActions.js).
         if (await recoverFromNoSubscription(failure, organizationId)) {
            notification.warning({
               message: "Heads up!",
               description: NO_SUBSCRIPTION_LEFT_MESSAGE
            });
            return;
         }

         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      } finally {
         setIsUndoing(false);
      }
   };

   return (
      <div className="billing-notice">
         <div className="billing-notice__text">
            <b>Your subscription is set to end</b>
            <span>
               {isBilledUntilScheduledEnd(status)
                  ? `Active until ${endsAt}. Your annual plan is billed monthly until then, and won't renew after that.`
                  : endsAt
                  ? `Active until ${endsAt}. It won't renew.`
                  : "It ends with the current period, and won't renew."}
            </span>
         </div>
         <Button className="kapSurveyPrimaryButton" onClick={keepSubscription} loading={isUndoing}>
            Keep my subscription
         </Button>
      </div>
   );
}

// A plan change waiting for the end of the contract — a downgrade, or monthly →
// annual on the same packet (planChanges.js) — which the server applies then.
// Until then the plan in force and its limits stay as they are, and the change
// can be dropped. A change picked while the subscription is paused is not one
// of these: the backend keeps it for when a card is added and doesn't report
// it. Only with the plan-change rules switched on (planDowngrades.js). The old
// flow's page shows it too, from its subscription read, with `rememberStatus`
// off.
export function PendingPlanChangeNotice({
   organizationId,
   status,
   onChanged,
   rememberStatus = true
}) {
   const [isDropping, setIsDropping] = useState(false);
   const change = status.pendingPlanChange;
   const effectiveAt = formatBillingDate(change.effectiveAt);
   const plan = change.packetName ? `the ${change.packetName} plan` : "your new plan";
   const billing =
      typeof change.isMonthly === "boolean"
         ? change.isMonthly
            ? ", billed monthly"
            : ", billed monthly on a 12-month commitment"
         : "";

   const keepCurrentPlan = async () => {
      if (isDropping) return;
      setIsDropping(true);

      try {
         await billingApi.cancelPlanChange({ organizationId }, { rememberStatus });
         notification.success({
            message: "Plan kept successfully",
            description:
               "Your current plan carries on, and nothing changes when your contract ends."
         });
         onChanged?.();
      } catch (failure) {
         if (await recoverFromNoSubscription(failure, organizationId)) {
            notification.warning({
               message: "Heads up!",
               description: NO_SUBSCRIPTION_LEFT_MESSAGE
            });
            return;
         }

         notification.error({
            message: "Oops! A problem has occurred!",
            description: describeBillingError(failure)
         });
      } finally {
         setIsDropping(false);
      }
   };

   return (
      <div className="billing-notice">
         <div className="billing-notice__text">
            <b>Your plan is set to change</b>
            <span>
               {effectiveAt
                  ? `On ${effectiveAt} you move to ${plan}${billing}. Until then, nothing changes.`
                  : `You move to ${plan}${billing} when your contract ends. Until then, nothing changes.`}
            </span>
         </div>
         <Button className="kapSurveyPrimaryButton" onClick={keepCurrentPlan} loading={isDropping}>
            Keep my current plan
         </Button>
      </div>
   );
}

function SubscriptionBillingNotices({ organizationId, status, onAddCard, onChanged }) {
   if (!status) return null;

   // An incomplete subscription is one whose first payment is still owed, and the
   // backend forgives it for a grace period the same as a missed renewal.
   const isPaymentDue =
      status.requiresAction ||
      status.subscriptionStatus === SubscriptionStatus.PAST_DUE ||
      status.subscriptionStatus === SubscriptionStatus.INCOMPLETE;

   return (
      <>
         {isPaymentDue && (
            <PaymentDueNotice
               organizationId={organizationId}
               status={status}
               onAddCard={onAddCard}
               onChanged={onChanged}
            />
         )}
         {!isPaymentDue && status.subscriptionStatus === SubscriptionStatus.TRIALING && (
            <TrialNotice status={status} onAddCard={onAddCard} />
         )}
         {isCancellationScheduled(status) && (
            <EndingNotice organizationId={organizationId} status={status} onChanged={onChanged} />
         )}
         {isPlanDowngradesEnabled() &&
            !isCancellationScheduled(status) &&
            status.pendingPlanChange?.packetId && (
               <PendingPlanChangeNotice
                  organizationId={organizationId}
                  status={status}
                  onChanged={onChanged}
               />
            )}
      </>
   );
}

export default SubscriptionBillingNotices;
